<script lang="ts" module>
	import type { SEOData } from '$lib/components/seo/types';

	export function seoIsNoIndex(seoData: SEOData | undefined) {
		return !seoData?.header?.allowIndex;
	}
</script>

<script lang="ts">
	import type { Locale } from '$lib/i18n/types';
	import defaultOGImage from '$assets/logos/logo-social.png';
	import { getAlternateHostnames, i18nFullHref } from '$lib/i18n/routing';
	import { type AvailableLanguageTag } from '$paraglide/runtime';
	import HeadStructuredSchema from './HeadStructuredSchema.svelte';

	/**
	 * Note about the ideal head order:
	 * 1. <title>
	 * 2. preconnect links
	 * 3  Didomi script
	 * 4. gtag script
	 * 5. dns-prefetch links
	 * 6. prefetch images
	 * 7. <meta name="description">
	 * 8. <meta property="og:type">
	 * 9. <meta property="og:site_name">
	 * 9. <meta property="og:title">
	 * 10. <meta property="og:description">
	 * 11. <meta property="og:image">
	 * 12. <meta property="og:url">
	 * 13. <link rel="canonical">
	 * 14. <link rel="prev">
	 * 15. <link rel="next">
	 * 16. <link rel="alternate">
	 * 17. rest of the head
	 */

	interface Props {
		seo: SEOData | undefined;
		url: URL;
		lang: AvailableLanguageTag;
	}

	const { seo, url, lang }: Props = $props();

	const og = $derived(seo?.header?.og);

	interface Link {
		href: string;
		rel: 'canonical' | 'alternate' | 'prev' | 'next';
		hreflang?: string;
	}
	const links: Link[] = $derived(seo ? buildLinks(seo, lang, url) : []);

	function locale2hreflang(locale: Locale): string {
		if (locale === 'en-US') {
			return 'en'; // special case, its not en-EN, but still we want only en
		}
		const lc = locale.toLowerCase();
		const [lang, region] = lc.split('-');
		if (lang === region) {
			return lang;
		} else {
			return lc;
		}
	}

	function buildAlternateLinks(seo: SEOData, url: URL) {
		const links: Link[] = [];

		if (seo.header?.allowIndex) {
			const localizedLinkSegments = seo.header.localizedLinkSegments;
			const pathWithoutAddress = seo.header.pathWithoutAddress;

			links.push(
				...getAlternateHostnames(url.hostname).map(([hostname, locale]) => {
					const path = localizedLinkSegments
						? `${pathWithoutAddress}/${localizedLinkSegments[locale]
							.map(link => link.value)
							.join('/')}`
						: pathWithoutAddress;

					return {
						rel: 'alternate' as const,
						href:
							i18nFullHref(
								path,
								locale.split('-', 1)[0] as AvailableLanguageTag,
								`https://${hostname}`,
							) + url.search,
						hreflang: locale2hreflang(locale),
					};
				}),
			);
		}

		return links;
	}
	function absoluteUrl(path: string) {
		if (path.match(/^https?:\/\//)) {
			return path;
		}
		return new URL(path, url).toString();
	}

	function buildLinks(seo: SEOData, lang: AvailableLanguageTag, url: URL) {
		const links: Link[] = [];

		if (seo.header?.showAlternates) {
			links.push(...buildAlternateLinks(seo, url));
		}

		const canonicalLink = seo.header?.canonicalLink;

		if (canonicalLink) {
			links.push({
				href: i18nFullHref(canonicalLink, lang, url.origin),
				rel: 'canonical',
			});
		}

		if (seo.header?.prevLink) {
			links.push({
				href: seo.header.prevLink,
				rel: 'prev',
			});
		}

		if (seo.header?.nextLink) {
			links.push({
				href: seo.header.nextLink,
				rel: 'next',
			});
		}

		return links;
	}
</script>

<svelte:head>
	{#if seoIsNoIndex(seo)}
		<meta name="robots" content="noindex, nofollow" />
	{/if}
	<title>{og?.title || 'ViaMichelin'}</title>
	{#if seo?.header?.preconnectOrigins}
		{#each seo?.header?.preconnectOrigins as origin (origin)}
			<link rel="preconnect" href={origin} />
			<link rel="dns-prefetch" href={origin} />
		{/each}
	{/if}
	{#if seo?.header?.preloadImages}
		{#each seo?.header?.preloadImages as img (img)}
			<link rel="prefetch" href={img} as="image" />
		{/each}
	{/if}
	<meta name="description" content={og?.description || 'ViaMichelin website'} />
	<meta property="og:type" content="website" />
	<meta property="og:site_name" content="ViaMichelin" />
	{#if og?.title}
		<meta property="og:title" content={og.title} />
	{/if}
	{#if og?.description}
		<meta property="og:description" content={og.description} />
	{/if}
	{#if og?.image || og?.title}
		<meta property="og:image" content={absoluteUrl(og.image ?? defaultOGImage)} />
	{/if}
	{#if seo?.header?.canonicalLink}
		<meta property="og:url" content={i18nFullHref(seo?.header?.canonicalLink, lang, url.origin)} />
	{/if}
	{#each links as link (link.href)}
		<link {...link} />
	{/each}
	{#if seo?.header?.structuredDatas}
		<HeadStructuredSchema schema={seo.header.structuredDatas} />
	{/if}
</svelte:head>
